import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, Edit3 } from "lucide-react";

const CATEGORIES = ["Maintenance", "Food", "Wi-Fi", "Cleanliness", "Other"];
const STATUSES = ["Pending", "In Progress", "Resolved"];

export default function ComplaintEditDialog({ open, onOpenChange, complaint, onSaved }) {
  const [form, setForm] = useState({
    category: "Maintenance",
    description: "",
    date_of_complaint: "",
    status: "Pending",
    admin_remarks: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !complaint) return;
    setForm({
      category: complaint.category || "Maintenance",
      description: complaint.description || "",
      date_of_complaint: complaint.date_of_complaint || "",
      status: complaint.status || "Pending",
      admin_remarks: complaint.admin_remarks || "",
    });
  }, [open, complaint]);

  const save = async () => {
    if (!form.description.trim()) {
      toast.error("Description can't be empty");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/complaints/${complaint.id}`, form);
      toast.success("Complaint updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg border-2 border-[#0A0A0A] shadow-brutal rounded-xl bg-white"
        data-testid="complaint-edit-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2.5 border-2 border-[#0A0A0A] rounded-xl bg-[#FFE600]">
              <Edit3 size={22} strokeWidth={2.5} />
            </div>
            <div>
              <DialogTitle className="font-heading font-black text-2xl tracking-tighter">
                Edit Complaint
              </DialogTitle>
              <DialogDescription className="text-sm font-medium text-[#4A4A4A]">
                {complaint?.student_name} · Flat {complaint?.flat_number || "-"}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">Category</span>
              <Select
                value={form.category}
                onValueChange={(v) => setForm({ ...form, category: v })}
              >
                <SelectTrigger
                  data-testid="complaint-edit-category"
                  className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-bold"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-2 border-[#0A0A0A]">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">Status</span>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger
                  data-testid="complaint-edit-status"
                  className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-bold"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-2 border-[#0A0A0A]">
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>

          <label className="block">
            <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">
              Date of Complaint
            </span>
            <input
              type="date"
              data-testid="complaint-edit-date"
              value={form.date_of_complaint}
              onChange={(e) => setForm({ ...form, date_of_complaint: e.target.value })}
              className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
            />
          </label>

          <label className="block">
            <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">
              Description
            </span>
            <textarea
              data-testid="complaint-edit-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
            />
          </label>

          <label className="block">
            <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">
              Admin remarks
            </span>
            <textarea
              data-testid="complaint-edit-remarks"
              value={form.admin_remarks}
              onChange={(e) => setForm({ ...form, admin_remarks: e.target.value })}
              rows={2}
              className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
              placeholder="Response / actions taken..."
            />
          </label>
        </div>

        <DialogFooter className="mt-4 gap-3">
          <button
            onClick={() => onOpenChange(false)}
            data-testid="complaint-edit-cancel"
            className="px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-white"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="complaint-edit-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60"
          >
            <Save size={16} strokeWidth={2.5} />
            {saving ? "Saving..." : "Save changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
